import { FC, ReactNode } from "react";
import { Skeleton, Stack, Typography } from "@mui/material";
import { SxProps } from "@mui/system";
import { TypographyWithTooltip } from "../TypographyWithTooltip";
import { CopyIconButton } from "@/components/common/CopyIconButton";

interface KeyValueSplitChipProps {
  title: ReactNode;
  value?: ReactNode;
  loading?: boolean;
  children?: ReactNode;
  keySx?: SxProps;
  valueSx?: SxProps;
  copyIconSx?: SxProps;
  copyText?: string;
  hideCopyIcon?: boolean;
  skeletonWidth?: number | string;
}

export const KeyValueSplitChip: FC<KeyValueSplitChipProps> = ({
  value,
  title,
  loading,
  children,
  keySx,
  valueSx,
  copyIconSx,
  copyText,
  hideCopyIcon,
  skeletonWidth = 150,
}) => {
  copyText = !copyText && typeof value === "string" ? value : "";

  const child = children ?? (
    <TypographyWithTooltip
      title={value ?? "N/A"}
      variant="body2"
      fontWeight="medium"
      whiteSpace="nowrap"
    />
  );

  return (
    <Stack
      direction="row"
      border={1}
      overflow="hidden"
      borderRadius={1.5}
      borderColor="grey.300"
      alignItems="center"
    >
      <Stack
        bgcolor="grey.100"
        py={0.5}
        px={1.25}
        sx={keySx}
        height="100%"
        justifyContent="center"
        borderRight={1}
        borderColor="grey.300"
      >
        <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
          {title}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        bgcolor="white"
        py={loading ? 0.25 : 0.5}
        sx={valueSx}
        px={1.25}
        alignItems="center"
      >
        {loading ? (
          <Skeleton variant="text" height={24} width={skeletonWidth} />
        ) : (
          <Stack>{child}</Stack>
        )}

        {copyText && !hideCopyIcon && !loading && (
          <CopyIconButton
            data={copyText}
            sx={{ p: 0.2, ...copyIconSx }}
            TooltipProps={{ placement: "top" }}
          />
        )}
      </Stack>
    </Stack>
  );
};
